import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getParametres, getProfile } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { usePlatform } from '../hooks/usePlatform'
import { useTheme } from '../hooks/useTheme'
import { useConfirm } from './ConfirmProvider'
import Avatar from './Avatar'
import Icon from './Icon'
import Logo, { Wordmark } from './Logo'

const NAV = [
  { to: '/dashboard', label: 'Tableau de bord', short: 'Tableau', icon: 'dashboard' },
  { to: '/operations', label: 'Opérations', short: 'Opérations', icon: 'operations' },
  { to: '/graphiques', label: 'Graphiques', short: 'Graphiques', icon: 'chart' },
  { to: '/parametres', label: 'Paramètres', short: 'Paramètres', icon: 'settings' },
]

/** Menu du compte : profil, devise et déconnexion regroupés sous l'avatar. */
function AccountMenu({ user, profile, devise, onLogout }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Un clic à l'extérieur ou Échap referme : sans cela, le menu reste
  // ouvert derrière la navigation.
  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const displayName = profile?.prenom || user?.username || 'Mon compte'

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md py-1 pl-1 pr-1.5 transition-colors duration-fast hover:bg-surface-hover sm:pr-2"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu du compte"
      >
        <Avatar avatar={profile?.avatar} username={user?.username} prenom={profile?.prenom} size="sm" />
        <span className="hidden max-w-[9rem] truncate text-base font-medium text-ink sm:inline">
          {displayName}
        </span>
        <Icon name="chevronDown" size={16} className="hidden text-ink-muted sm:block" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1.5 w-60 origin-top-right overflow-hidden rounded-lg border border-line bg-surface shadow-lg animate-slide-up"
        >
          <div className="border-b border-line px-3 py-3">
            <p className="truncate text-base font-medium text-ink">{displayName}</p>
            <p className="truncate text-sm text-ink-muted">{user?.email || user?.username}</p>
          </div>

          <div className="p-1">
            <Link
              to="/profil"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-base text-ink-secondary transition-colors duration-fast hover:bg-surface-hover hover:text-ink"
            >
              <Icon name="user" size={16} />
              Mon profil
            </Link>
            <Link
              to="/parametres"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-base text-ink-secondary transition-colors duration-fast hover:bg-surface-hover hover:text-ink"
            >
              <Icon name="settings" size={16} />
              Paramètres
              <span className="ml-auto text-sm text-ink-muted">{devise}</span>
            </Link>
          </div>

          <div className="border-t border-line p-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onLogout()
              }}
              className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-base text-negative transition-colors duration-fast hover:bg-negative-subtle"
            >
              <Icon name="logout" size={16} />
              Se déconnecter
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isAndroid } = usePlatform()
  const { isDark, toggle } = useTheme()
  const confirm = useConfirm()

  const [devise, setDevise] = useState('EUR')
  const [profile, setProfile] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    let cancelled = false

    getParametres()
      .then((params) => {
        if (!cancelled && params?.devise) setDevise(params.devise)
      })
      .catch(() => {
        /* la devise garde sa valeur par défaut */
      })

    const loadProfile = () => {
      if (!user) return
      getProfile()
        .then((res) => {
          if (!cancelled && res?.success) setProfile(res.user)
        })
        .catch(() => {
          /* l'avatar retombe sur l'initiale */
        })
    }

    loadProfile()
    window.addEventListener('profileUpdated', loadProfile)
    return () => {
      cancelled = true
      window.removeEventListener('profileUpdated', loadProfile)
    }
  }, [user])

  // La navigation referme le menu mobile : sinon il masque la page d'arrivée.
  useEffect(() => setMenuOpen(false), [location.pathname])

  const isActive = (path) =>
    location.pathname === path || (path === '/dashboard' && location.pathname === '/')

  // window.confirm() gelait la page et sortait du design de l'app.
  const handleLogout = useCallback(async () => {
    const ok = await confirm({
      title: 'Se déconnecter ?',
      description: 'Vos données restent enregistrées. Vous devrez saisir vos identifiants à la prochaine visite.',
      confirmLabel: 'Se déconnecter',
    })
    if (!ok) return
    await logout()
    navigate('/login', { replace: true })
  }, [confirm, logout, navigate])

  return (
    <header className="app-header">
      <div className="app-header-inner">
        {/* Sur Android, la navigation est en bas : pas de menu ici. */}
        {!isAndroid && (
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="btn-icon -ml-1 md:hidden"
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} size={20} />
          </button>
        )}

        <Link
          to="/dashboard"
          className="flex items-center gap-2.5 rounded-md"
          aria-label="Finance Prévision, aller au tableau de bord"
        >
          <Logo size={26} />
          <Wordmark className="hidden text-lg text-ink sm:inline-flex" />
        </Link>

        {!isAndroid && (
          <nav className="ml-4 hidden items-center gap-0.5 md:flex" aria-label="Navigation principale">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`nav-link ${isActive(item.to) ? 'nav-link-active' : ''}`}
                aria-current={isActive(item.to) ? 'page' : undefined}
              >
                <Icon name={item.icon} size={16} />
                {item.short}
              </Link>
            ))}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-1">
          <span className="hidden rounded-sm bg-surface-sunken px-2 py-1 text-sm font-medium text-ink-secondary lg:inline">
            {devise}
          </span>
          <button
            type="button"
            onClick={toggle}
            className="btn-icon"
            aria-label={isDark ? 'Passer en thème clair' : 'Passer en thème sombre'}
            title={isDark ? 'Thème clair' : 'Thème sombre'}
          >
            <Icon name={isDark ? 'sun' : 'moon'} size={18} />
          </button>
          {user && (
            <AccountMenu user={user} profile={profile} devise={devise} onLogout={handleLogout} />
          )}
        </div>
      </div>

      {/* Menu mobile */}
      {menuOpen && !isAndroid && (
        <nav className="border-t border-line bg-surface md:hidden" aria-label="Navigation principale">
          <div className="mx-auto max-w-[1200px] space-y-0.5 px-3 py-2">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`nav-link w-full justify-start ${isActive(item.to) ? 'nav-link-active' : ''}`}
                aria-current={isActive(item.to) ? 'page' : undefined}
              >
                <Icon name={item.icon} size={18} />
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  )
}
